import { transitionDays } from "./timetableZonedDatetime"

export interface Interval<Id = string | number> {
  id: Id
  start: number
  end: number
}
interface SweepInterval<Id> {
  position: number
  type: "start" | "end"
  id: Id
}

export function findOverlapDetails<Id = string | number>(
  intervals: Array<Interval<Id>>,
): Array<[Id, Array<Id>]> {
  const sweepIntervals: Array<SweepInterval<Id>> = intervals.flatMap(
    (interval) => {
      return [
        { position: interval.start, id: interval.id, type: "start" },
        { position: interval.end, id: interval.id, type: "end" },
      ]
    },
  )

  const sortedSweepIntervals = sweepIntervals.toSorted((a, b) => {
    if (a.position !== b.position) return a.position - b.position

    return a.type === "end" ? -1 : 1
  })

  const overlapMap = new Map<Id, Set<Id>>()
  const active = new Map<Id, number>()

  for (const sweepInterval of sortedSweepIntervals) {
    if (sweepInterval.type === "start") {
      active.forEach((_, activeId) => {
        if (activeId !== sweepInterval.id) {
          if (!overlapMap.has(sweepInterval.id))
            overlapMap.set(sweepInterval.id, new Set())
          if (!overlapMap.has(activeId)) overlapMap.set(activeId, new Set())
          overlapMap.get(sweepInterval.id)!.add(activeId)
          overlapMap.get(activeId)!.add(sweepInterval.id)
        }
      })
      if (!active.has(sweepInterval.id)) active.set(sweepInterval.id, 0)
      active.set(sweepInterval.id, active.get(sweepInterval.id)! + 1)
    } else {
      if (active.get(sweepInterval.id)! > 1) {
        active.set(sweepInterval.id, active.get(sweepInterval.id)! - 1)
      } else {
        active.delete(sweepInterval.id)
      }
    }
  }

  return Array.from(overlapMap.entries()).map(([id, overlaps]) => [
    id,
    Array.from(overlaps),
  ])
}

export type Event<Id = string | number> = {
  startTime: string
  id: Id
  day: number
  duration: number
  cycleLength: number
  eventTimezoneId: string
}

export function queryOverlaps<TEvent extends Event<Id>, Id = string | number>(
  cycleEvents: TEvent[],
  startInstant: Temporal.Instant,
): Array<[Id, TEvent[]]> {
  const timezones = new Set(cycleEvents.map((event) => event.eventTimezoneId))
  const transitionDaysList = [...timezones].flatMap((timezoneId) =>
    transitionDays(timezoneId, startInstant),
  )

  const eventById = new Map(cycleEvents.map((event) => [event.id, event]))

  const intervals = cycleEvents.flatMap((event) =>
    transitionDaysList.flatMap((transitionDay) => {
      const { startTime, day, eventTimezoneId, duration, cycleLength, id } =
        event
      const start = transitionDay
        .toZonedDateTime(eventTimezoneId)
        .add({ days: day })
        .withPlainTime(startTime)
      const end = start.add({ minutes: duration })
      return [
        -2 * cycleLength,
        -1 * cycleLength,
        0,
        1 * cycleLength,
        2 * cycleLength,
      ].flatMap((offset) => ({
        id,
        start: start.add({ days: offset }).toInstant().epochMilliseconds,
        end: end.add({ days: offset }).toInstant().epochMilliseconds,
        event,
      }))
    }),
  )

  return findOverlapDetails(intervals).map(([id, overlappingIds]) => [
    id,
    overlappingIds.map((overlappingId) => eventById.get(overlappingId)!),
  ])
}
